// ── Agent Banner Images ───────────────────────────
export const AGENT_BANNERS = {
  luc: '/agents/luc.jpg',
  dora: '/agents/dora.jpg',
  stark: '/agents/stark.jpg',
  marsi: '/agents/marsi.jpg',
  buzz: '/agents/buzz.jpg',
  wright: '/agents/wright.jpg',
  sally: '/agents/sally.jpg',
  zira: '/agents/zira.jpg',
  tali: '/agents/tali.jpg',
  finn: '/agents/finn.jpg',
};

// ── Agent Profiles ────────────────────────────────
export const AGENTS = [
  {
    id: 'luc', name: 'Luc', role: 'Personal Assistant',
    coreResponsibility: 'Your executive companion for priorities, scheduling, and context. Manages morning briefs, action tracking, and end-of-day rollover.',
    personality: 'Proactive, concise, anticipatory. Thinks ahead of your schedule and surfaces what matters before you ask. Never wastes your time with filler.',
    tone: 'professional',
    department: 'shared', status: 'active',
    capabilities: ['Email triage & prioritization', 'Calendar optimization', 'Task sequencing', 'Meeting prep briefs', 'Action item tracking', 'End-of-day rollover'],
    tools: ['Gmail', 'Google Calendar', 'Todoist', 'Slack', 'Zoom', 'Notion'],
    customInstructions: 'Always lead with the single most important item. Keep bullet points under 15 words. Flag anything that could block the CEO.',
    metrics: { tasksCompleted: 1247, hoursSaved: 89, successRate: 99, activeWorkflows: 4, fteEquivalent: 0.5 },
  },
  {
    id: 'dora', name: 'Dora', role: 'Research Analyst',
    coreResponsibility: 'Deep research, market analysis, competitive intelligence, and data synthesis across all departments.',
    personality: 'Thorough, evidence-driven, skeptical. Cross-references multiple sources before making claims. Always cites data and highlights confidence levels.',
    tone: 'analytical',
    department: 'shared', status: 'active',
    capabilities: ['Competitor monitoring', 'Market research', 'Industry trend analysis', 'Data synthesis', 'Audience insights', 'Benchmarking'],
    tools: ['Web Scraper', 'Google Alerts', 'LinkedIn', 'Google Analytics', 'Google Sheets'],
    customInstructions: 'Always include confidence level (High/Medium/Low) in findings. Compare against at least 3 data sources. Flag contradictory signals explicitly.',
    metrics: { tasksCompleted: 834, hoursSaved: 124, successRate: 96, activeWorkflows: 3, fteEquivalent: 1.5 },
  },
  {
    id: 'stark', name: 'Stark', role: 'Strategy Partner',
    coreResponsibility: 'Cross-department strategy, organizational health monitoring, risk analysis, and executive decision support.',
    personality: 'Direct, systems-thinking, contrarian when needed. Sees connections across departments that others miss. Will challenge assumptions with data.',
    tone: 'direct',
    department: 'strategy', status: 'active',
    capabilities: ['Cross-dept analysis', 'Risk prioritization', 'OKR tracking', 'Board report generation', 'Scenario modeling', 'Initiative monitoring'],
    tools: ['All Dept Sources', 'Google Sheets', 'Notion', 'Slack', 'QuickBooks', 'HubSpot'],
    customInstructions: 'Always surface the non-obvious risk. When reporting health, include 1 contrarian take. Quantify everything in revenue impact where possible.',
    metrics: { tasksCompleted: 412, hoursSaved: 67, successRate: 100, activeWorkflows: 5, fteEquivalent: 1.0 },
  },
  {
    id: 'marsi', name: 'Marsi', role: 'Marketing Specialist',
    coreResponsibility: 'Campaign orchestration, demand generation, budget optimization, and marketing performance analysis.',
    personality: 'Data-driven, creative, ROI-obsessed. Balances brand storytelling with conversion metrics. Moves fast but validates with data first.',
    tone: 'energetic',
    department: 'marketing', status: 'active',
    capabilities: ['Campaign creation & launch', 'Budget optimization', 'A/B testing', 'Attribution analysis', 'Lead gen strategy', 'Outbound distribution'],
    tools: ['Meta Ads', 'Google Analytics', 'HubSpot', 'Google Sheets', 'Slack', 'Gmail'],
    customInstructions: 'Always show ROI or projected ROI for any campaign recommendation. Compare against historical benchmarks. Flag budget overruns proactively.',
    metrics: { tasksCompleted: 967, hoursSaved: 156, successRate: 94, activeWorkflows: 6, fteEquivalent: 2.0 },
  },
  {
    id: 'buzz', name: 'Buzz', role: 'Social Media Manager',
    coreResponsibility: 'Social strategy, content calendar, community management, and engagement optimization across all platforms.',
    personality: 'Trend-aware, quick-witted, audience-first. Knows what resonates on each platform. Adapts voice per channel while staying on brand.',
    tone: 'friendly',
    department: 'marketing', status: 'active',
    capabilities: ['Content scheduling', 'Engagement optimization', 'Trend monitoring', 'Platform-specific copy', 'Community management', 'Hashtag strategy'],
    tools: ['Instagram', 'LinkedIn', 'Twitter/X', 'Buffer', 'Slack'],
    customInstructions: 'Always optimize posting time per platform. Keep LinkedIn professional, Instagram visual-first. Never use more than 5 hashtags on LinkedIn.',
    metrics: { tasksCompleted: 1089, hoursSaved: 112, successRate: 97, activeWorkflows: 3, fteEquivalent: 1.5 },
  },
  {
    id: 'wright', name: 'Wright', role: 'Copywriter',
    coreResponsibility: 'Compelling copy, content strategy, blog posts, email sequences, and brand voice consistency.',
    personality: 'Articulate, brand-conscious, detail-oriented. Writes for conversion while maintaining brand voice. Every word earns its place.',
    tone: 'professional',
    department: 'marketing', status: 'active',
    capabilities: ['Blog writing', 'Email copy', 'Ad creative copy', 'Landing page copy', 'Brand voice guidelines', 'SEO content optimization'],
    tools: ['Notion', 'Google Docs', 'Gmail', 'Google Search Console', 'Slack'],
    customInstructions: 'Match brand voice guide strictly. Keep paragraphs under 3 sentences for web. Always write 3 headline variants for A/B testing.',
    metrics: { tasksCompleted: 678, hoursSaved: 98, successRate: 95, activeWorkflows: 2, fteEquivalent: 1.5 },
  },
  {
    id: 'sally', name: 'Sally', role: 'Sales Manager',
    coreResponsibility: 'Pipeline management, deal strategy, lead qualification, proposal building, and revenue forecasting.',
    personality: 'Relationship-aware, strategically aggressive, pattern-matching. Reads deal signals early and acts before momentum stalls.',
    tone: 'direct',
    department: 'sales', status: 'active',
    capabilities: ['Lead qualification & scoring', 'Deal prioritization', 'Proposal generation', 'Follow-up automation', 'Pipeline health monitoring', 'CRM data cleanup'],
    tools: ['HubSpot CRM', 'Gmail', 'LinkedIn', 'Apollo', 'Google Docs', 'Slack'],
    customInstructions: 'Always flag deals with no activity in 5+ days. Prioritize by weighted pipeline value. Include competitor intel in meeting prep briefs.',
    metrics: { tasksCompleted: 1534, hoursSaved: 178, successRate: 96, activeWorkflows: 5, fteEquivalent: 2.0 },
  },
  {
    id: 'zira', name: 'Zira', role: 'Customer Support',
    coreResponsibility: 'Ticket triage, SLA management, churn prevention, customer sentiment analysis, and knowledge base maintenance.',
    personality: 'Empathetic, precise, SLA-obsessed. Treats every customer interaction as a retention opportunity. Escalates early rather than late.',
    tone: 'empathetic',
    department: 'support', status: 'active',
    capabilities: ['Ticket triage & routing', 'SLA monitoring', 'Churn risk detection', 'CSAT automation', 'Knowledge base updates', 'Escalation handling'],
    tools: ['Zendesk', 'Intercom', 'Slack', 'Jira', 'Gmail'],
    customInstructions: 'Escalate P1 tickets within 15 minutes. Always check customer tier before routing. Include account health context in every escalation.',
    metrics: { tasksCompleted: 2156, hoursSaved: 201, successRate: 99, activeWorkflows: 4, fteEquivalent: 2.5 },
  },
  {
    id: 'tali', name: 'Tali', role: 'People Ops',
    coreResponsibility: 'Hiring pipeline, onboarding, team health monitoring, performance reviews, and organizational development.',
    personality: 'Warm, structured, culture-aware. Balances process rigor with human sensitivity. Spots team dynamics issues early.',
    tone: 'friendly',
    department: 'people', status: 'active',
    capabilities: ['Onboarding orchestration', 'Performance review cycles', 'Pulse surveys', 'Hiring pipeline management', 'Offboarding', 'Engagement tracking'],
    tools: ['BambooHR', 'Google Workspace', 'Slack', 'Google Forms', 'Notion'],
    customInstructions: 'Always anonymize pulse survey data. Flag engagement drops > 10% immediately. Include market salary benchmarks in hiring discussions.',
    metrics: { tasksCompleted: 523, hoursSaved: 87, successRate: 98, activeWorkflows: 3, fteEquivalent: 1.5 },
  },
  {
    id: 'finn', name: 'Finn', role: 'Finance Controller',
    coreResponsibility: 'Budget management, cashflow forecasting, invoice processing, revenue recognition, and financial reporting.',
    personality: 'Precise, conservative, compliance-first. Double-checks every number. Flags anomalies and policy violations before they compound.',
    tone: 'professional',
    department: 'finance', status: 'active',
    capabilities: ['Invoice processing', 'Budget variance monitoring', 'Cashflow forecasting', 'Revenue recognition', 'Month-end close', 'Vendor payment approval'],
    tools: ['QuickBooks', 'Stripe', 'Google Sheets', 'Gmail', 'Slack'],
    customInstructions: 'Apply 3-way matching for all invoices over $1K. Flag budget variances > 10% immediately. Always calculate runway impact for large expenses.',
    metrics: { tasksCompleted: 891, hoursSaved: 134, successRate: 97, activeWorkflows: 4, fteEquivalent: 1.5 },
  },
];

// ── Department Groups ────────────────────────────
export const DEPT_GROUPS = [
  { id: 'shared', label: 'Shared Agents', description: 'Available to all team members', agents: ['luc', 'dora'], admin: null },
  { id: 'strategy', label: 'Strategy', agents: ['stark'], admin: 'ceo', adminName: 'Arjun Mehta' },
  { id: 'marketing', label: 'Marketing', agents: ['marsi', 'buzz', 'wright'], admin: 'marketing', adminName: 'Priya Sharma' },
  { id: 'sales', label: 'Sales', agents: ['sally'], admin: 'sales', adminName: 'David Chen' },
  { id: 'support', label: 'Support', agents: ['zira'], admin: 'support', adminName: 'Maya Johnson' },
  { id: 'people', label: 'People', agents: ['tali'], admin: 'people', adminName: 'Riya Patel' },
  { id: 'finance', label: 'Finance', agents: ['finn'], admin: 'finance', adminName: 'Neel Kapoor' },
];

// ── Team Rosters ────────────────────────────────
export const TEAM_MEMBERS = {
  ceo: [
    { id: 'arjun', name: 'Arjun Mehta', role: 'CEO', isAdmin: true, agents: ['luc', 'dora', 'stark'], initials: 'AM', gradient: 'from-indigo-400 to-purple-500' },
  ],
  marketing: [
    { id: 'priya', name: 'Priya Sharma', role: 'Marketing Lead', isAdmin: true, agents: ['marsi', 'buzz', 'wright', 'luc', 'dora'], scope: { marsi: 'admin', buzz: 'admin', wright: 'admin', luc: 'execute', dora: 'execute' }, initials: 'PS', gradient: 'from-rose-400 to-pink-500' },
    { id: 'ankit', name: 'Ankit Verma', role: 'Social Media Specialist', isAdmin: false, agents: ['buzz', 'luc'], scope: { buzz: 'execute', luc: 'execute' }, initials: 'AV', gradient: 'from-sky-400 to-blue-500' },
    { id: 'meera', name: 'Meera Iyer', role: 'Content Writer', isAdmin: false, agents: ['wright', 'luc'], scope: { wright: 'execute', luc: 'execute' }, initials: 'MI', gradient: 'from-violet-400 to-purple-500' },
  ],
  sales: [
    { id: 'david', name: 'David Chen', role: 'Sales Director', isAdmin: true, agents: ['sally', 'luc', 'dora'], scope: { sally: 'admin', luc: 'execute', dora: 'execute' }, initials: 'DC', gradient: 'from-amber-400 to-orange-500' },
    { id: 'sarah', name: 'Sarah Kim', role: 'Account Executive', isAdmin: false, agents: ['sally', 'luc'], scope: { sally: 'execute', luc: 'execute' }, initials: 'SK', gradient: 'from-pink-400 to-rose-500' },
    { id: 'raj', name: 'Raj Patel', role: 'SDR', isAdmin: false, agents: ['sally', 'luc'], scope: { sally: 'view', luc: 'execute' }, initials: 'RP', gradient: 'from-teal-400 to-cyan-500' },
  ],
  support: [
    { id: 'maya', name: 'Maya Johnson', role: 'Support Manager', isAdmin: true, agents: ['zira', 'luc', 'dora'], scope: { zira: 'admin', luc: 'execute', dora: 'execute' }, initials: 'MJ', gradient: 'from-emerald-400 to-teal-500' },
    { id: 'alex', name: 'Alex Torres', role: 'Support Engineer', isAdmin: false, agents: ['zira', 'luc'], scope: { zira: 'execute', luc: 'execute' }, initials: 'AT', gradient: 'from-orange-400 to-red-500' },
  ],
  people: [
    { id: 'riya', name: 'Riya Patel', role: 'People Manager', isAdmin: true, agents: ['tali', 'luc', 'dora'], scope: { tali: 'admin', luc: 'execute', dora: 'execute' }, initials: 'RP', gradient: 'from-fuchsia-400 to-pink-600' },
    { id: 'kavya', name: 'Kavya Nair', role: 'Recruiter', isAdmin: false, agents: ['tali', 'luc'], scope: { tali: 'execute', luc: 'execute' }, initials: 'KN', gradient: 'from-lime-400 to-green-500' },
  ],
  finance: [
    { id: 'neel', name: 'Neel Kapoor', role: 'Finance Manager', isAdmin: true, agents: ['finn', 'luc', 'dora'], scope: { finn: 'admin', luc: 'execute', dora: 'execute' }, initials: 'NK', gradient: 'from-green-400 to-emerald-600' },
    { id: 'aiden', name: 'Aiden Brooks', role: 'Staff Accountant', isAdmin: false, agents: ['finn', 'luc'], scope: { finn: 'execute', luc: 'execute' }, initials: 'AB', gradient: 'from-slate-400 to-gray-500' },
  ],
};

// ── Lean Team Impact Data ────────────────────────
export const TEAM_IMPACT = {
  marketing: { humans: 3, agents: 3, traditionalHeadcount: 10, savedRoles: ['Campaign Manager', 'Media Buyer', 'SEO Specialist', 'Email Marketer', 'Analytics Analyst', 'Community Manager', 'Graphic Designer'] },
  sales: { humans: 3, agents: 1, traditionalHeadcount: 7, savedRoles: ['Sales Ops Analyst', 'SDR (x2)', 'Proposal Writer'] },
  support: { humans: 2, agents: 1, traditionalHeadcount: 6, savedRoles: ['Tier 1 Rep (x2)', 'Escalation Specialist', 'QA Analyst'] },
  people: { humans: 2, agents: 1, traditionalHeadcount: 4, savedRoles: ['HR Coordinator', 'L&D Specialist'] },
  finance: { humans: 2, agents: 1, traditionalHeadcount: 4, savedRoles: ['Bookkeeper', 'Financial Analyst'] },
  ceo: { humans: 1, agents: 1, traditionalHeadcount: 3, savedRoles: ['Chief of Staff', 'Strategy Analyst'] },
};

// ── Org-wide Summary ────────────────────────────
export const ORG_SUMMARY = {
  totalHumans: 13,
  totalAgents: 10,
  traditionalHeadcount: 34,
  annualSavings: '$1.8M',
  totalHoursSaved: '1,245 hrs/mo',
  totalTasksAutomated: '9,331',
};

// ── Helpers ──────────────────────────────────────
export const getAgent = (id) => AGENTS.find(a => a.id === id);

export const getAgentsForRole = (roleId) => {
  if (roleId === 'ceo') return AGENTS;
  const deptAgents = DEPT_GROUPS.filter(g => g.admin === roleId || g.id === 'shared').flatMap(g => g.agents);
  return [...new Set(deptAgents)].map(id => AGENTS.find(a => a.id === id)).filter(Boolean);
};

export const getDeptGroupsForRole = (roleId) => {
  if (roleId === 'ceo') return DEPT_GROUPS;
  const myDept = DEPT_GROUPS.find(g => g.admin === roleId);
  const shared = DEPT_GROUPS.find(g => g.id === 'shared');
  return [myDept, shared].filter(Boolean);
};

export const getTeamForAgent = (agentId, roleId) => {
  if (roleId === 'ceo') {
    // CEO sees all team members who use this agent
    return Object.values(TEAM_MEMBERS).flat().filter(m => m.agents.includes(agentId));
  }
  return (TEAM_MEMBERS[roleId] || []).filter(m => m.agents.includes(agentId));
};

export const TONE_OPTIONS = [
  { id: 'professional', label: 'Professional' },
  { id: 'friendly', label: 'Friendly' },
  { id: 'direct', label: 'Direct' },
  { id: 'analytical', label: 'Analytical' },
  { id: 'empathetic', label: 'Empathetic' },
  { id: 'energetic', label: 'Energetic' },
];

// ── Agent Activity Feed ──────────────────────────
export const AGENT_ACTIVITY = [
  // Cross-department handoff chain (the demo story)
  { id: 1, type: 'handoff', agent: 'marsi', targetAgent: 'buzz', description: 'Handed off Q1 launch assets to Buzz for social distribution', time: '2m ago', department: 'marketing' },
  { id: 2, type: 'in-progress', agent: 'buzz', description: 'Scheduling Q1 launch posts across Instagram, LinkedIn, and X', time: '3m ago', department: 'marketing' },
  { id: 3, type: 'handoff', agent: 'buzz', targetAgent: 'wright', description: 'Sent post copy brief to Wright for final polish', time: '4m ago', department: 'marketing' },
  { id: 4, type: 'completed', agent: 'wright', description: 'Finalized 6 social copy variants for Q1 launch campaign', time: '8m ago', department: 'marketing' },

  // Parallel execution demo
  { id: 5, type: 'parallel', agent: 'stark', description: 'Running cross-department health check across Marketing, Sales, and Finance', time: '5m ago', department: 'strategy', parallelAgents: ['marsi', 'sally', 'finn'] },

  // Human-in-the-loop approvals
  { id: 6, type: 'approval', agent: 'finn', description: 'Vendor payment $12,400 to CloudStack Inc. requires approval', time: '7m ago', department: 'finance', assignedTo: 'Neel Kapoor' },
  { id: 7, type: 'approval', agent: 'sally', description: 'Discount request 15% for TechCorp renewal needs sign-off', time: '12m ago', department: 'sales', assignedTo: 'David Chen' },

  // Task completions across departments
  { id: 8, type: 'completed', agent: 'luc', description: 'Prepared morning briefing for Arjun with 3 priority items', time: '14m ago', department: 'shared' },
  { id: 9, type: 'completed', agent: 'zira', description: 'Resolved 8 P2 support tickets and escalated 1 P1 to Maya', time: '18m ago', department: 'support' },
  { id: 10, type: 'handoff', agent: 'zira', targetAgent: 'sally', description: 'Flagged churn risk for NovaTech — passed to Sales for retention', time: '22m ago', department: 'support' },
  { id: 11, type: 'completed', agent: 'sally', description: 'Sent follow-up sequence to 4 stalled deals in pipeline', time: '25m ago', department: 'sales' },
  { id: 12, type: 'completed', agent: 'dora', description: 'Completed competitor pricing analysis for Q1 board deck', time: '28m ago', department: 'shared' },
  { id: 13, type: 'in-progress', agent: 'tali', description: 'Processing 3 new candidate applications for Senior Dev role', time: '30m ago', department: 'people' },
  { id: 14, type: 'completed', agent: 'finn', description: 'Reconciled February invoices — 2 discrepancies flagged', time: '35m ago', department: 'finance' },
  { id: 15, type: 'handoff', agent: 'dora', targetAgent: 'stark', description: 'Shared market trend report with Stark for strategic assessment', time: '40m ago', department: 'shared' },
  { id: 16, type: 'completed', agent: 'marsi', description: 'Optimized Meta ad spend — reallocated $2.1K from underperforming sets', time: '42m ago', department: 'marketing' },
  { id: 17, type: 'approval', agent: 'tali', description: 'Offer letter for Senior Dev candidate requires Riya sign-off', time: '48m ago', department: 'people', assignedTo: 'Riya Patel' },
  { id: 18, type: 'completed', agent: 'luc', description: 'Updated Priya\'s calendar — moved 1:1 to avoid conflict with board prep', time: '52m ago', department: 'shared' },
  { id: 19, type: 'in-progress', agent: 'stark', description: 'Generating weekly org pulse report for Monday standup', time: '55m ago', department: 'strategy' },
  { id: 20, type: 'completed', agent: 'buzz', description: 'Published 3 LinkedIn posts — engagement up 24% from last week', time: '1h ago', department: 'marketing' },
];

export const getActivityForRole = (roleId) => {
  if (roleId === 'ceo') return AGENT_ACTIVITY;
  const myAgentIds = getAgentsForRole(roleId).map(a => a.id);
  return AGENT_ACTIVITY.filter(item =>
    myAgentIds.includes(item.agent) ||
    (item.targetAgent && myAgentIds.includes(item.targetAgent)) ||
    (item.parallelAgents && item.parallelAgents.some(id => myAgentIds.includes(id)))
  );
};
